import { localDateKey } from '@arrow/game-engine';
import type { Level } from '@arrow/puzzle-schema';
import { useEffect, useState } from 'react';

import { navigate } from '../app/router.tsx';
import { fetchToday, type TodayBoard } from '../nests/api.ts';
import { placeLine } from '../nests/model.ts';
import { currentNest, useNestMemory, useNestsVisible } from '../nests/store.ts';
import { pushDailyForNest } from '../platform/sync.ts';
import { InstallIcon } from './icons.tsx';

/** How long to wait for the api to have the finish before asking again (it's sent as the poster opens). */
const TRIES_MS = [0, 1500, 3500, 7000];

export interface PosterDaily {
	number: number;
	level: Level;
	/** The daily's date: only today's (on the device's own date) gets a line. */
	dateKey: string;
}

/**
 * The poster's nest: whether this solve belongs to one (gate open, in a nest, today's
 * daily), and once the api has the finish, "2nd of 3 in Family today". `nest` is known at
 * once, so the poster can settle its buttons before the line arrives.
 */
export function usePosterNest(daily: PosterDaily | null, open: boolean): { nest: boolean; line: { text: string; spoken: string } | null } {
	const visible = useNestsVisible();
	const memory = useNestMemory();
	const target = currentNest(memory);
	const isToday = !!daily && daily.dateKey === localDateKey(new Date());
	const nest = visible && !!target && isToday;
	const [line, setLine] = useState<{ text: string; spoken: string } | null>(null);
	const key = nest && daily ? `${target!.id}:${daily.number}:${daily.level}` : null;

	useEffect(() => {
		setLine(null);
		if (!open || !key || !daily || !target) return;
		const ctrl = new AbortController();
		let timer: ReturnType<typeof setTimeout> | undefined;
		const attempt = (i: number) => {
			timer = setTimeout(async () => {
				try {
					if (i === 0) await pushDailyForNest({ number: daily.number, level: daily.level });
					const board = await fetchToday(target.id, daily.dateKey, ctrl.signal);
					if (ctrl.signal.aborted) return;
					const place = board.number === daily.number ? (placeLine(board, daily.level, target.name) ?? offlineLine(board, daily.level, target.name)) : null;
					if (place) setLine(place);
					else if (i + 1 < TRIES_MS.length) attempt(i + 1);
				} catch {
					if (!ctrl.signal.aborted && i + 1 < TRIES_MS.length) attempt(i + 1);
				}
			}, TRIES_MS[i]);
		};
		attempt(0);
		return () => {
			ctrl.abort();
			clearTimeout(timer);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, key]);

	return { nest, line };
}

/**
 * A solve whose start never reached the server finishes in the nest without a time (UX-064,
 * the nest's fair-play rule). Said once, here, so it doesn't look as if the time was lost.
 */
function offlineLine(board: TodayBoard, level: Level, nestName: string): { text: string; spoken: string } | null {
	const mine = board.levels.find((l) => l.level === level)?.entries.find((e) => e.memberId === board.me);
	if (mine?.state !== 'unverified') return null;
	return {
		text: `Started offline, so no time in ${nestName}`,
		spoken: `This one started offline, so it shows in ${nestName} without a time. Open the nest.`,
	};
}

/** The line itself: one tappable line under the time, the way on to the nest. */
export function PosterNestLine({ line }: { line: { text: string; spoken: string } }) {
	return (
		<button type="button" className="pline" onClick={() => navigate('/nest')} aria-label={line.spoken} data-testid="poster-nest">
			<span>{line.text}</span>
			<span className="mn-chev" aria-hidden="true">
				<InstallIcon.fwd />
			</span>
		</button>
	);
}
